import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import FormSelect from '../../components/Forms/FormSelect';
import Pagination from '../../components/UI/Pagination';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
// import ModalPane from '../../components/UI/ModalPane'; // Não está sendo usado
import PromptDetailsView from '../../components/Applications/PromptDetailsView';
import { 
  getApplicationById, 
  getApplicationPrompts, 
  deleteApplicationPrompt,
  getUserStatuses,
  getPromptDirectById
} from '../../services/applications/applicationsService';
import { ApplicationDto, SystemMessageDto } from '../../services/applications/types';

const PromptsList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [prompts, setPrompts] = useState<SystemMessageDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userStatus, setUserStatus] = useState<string>('');
  const [availableUserStatuses, setAvailableUserStatuses] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [selectedPrompt, setSelectedPrompt] = useState<SystemMessageDto | null>(null);
  const [promptToDelete, setPromptToDelete] = useState<SystemMessageDto | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const fetchData = useCallback(async (applicationId: string, status: string, page: number, size: number) => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view prompts.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both application details and prompts in parallel
      const [appData, promptsData] = await Promise.all([
        getApplicationById(auth.user || null, applicationId),
        getApplicationPrompts(auth.user || null, applicationId, status, page, size)
      ]);
      
      setApplication(appData);
      setPrompts(promptsData);
      
      // Estimate total pages based on whether we got a full page of results
      const receivedFullPage = promptsData && promptsData.length === size;
      const estimatedTotalPages = receivedFullPage ? 
        Math.max(page + 1, 2) : // If full page, at least one more page exists
        page; // Otherwise, this might be the last page
      
      setTotalPages(estimatedTotalPages);
    } catch (err) {
      setError('Failed to fetch prompts. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user]);
  
  // Carregar status de usuários disponíveis
  useEffect(() => {
    const loadUserStatuses = async () => {
      if (!auth.user) return;
      
      try {
        const statuses = await getUserStatuses(auth.user);
        setAvailableUserStatuses(statuses);
      } catch (error) {
        console.error("Error loading user statuses:", error);
        // Fallback será aplicado no componente de select
      }
    };
    
    loadUserStatuses();
  }, [auth.user]);
  
  // Carregar prompts quando necessário
  useEffect(() => {
    if (id) {
      fetchData(id, userStatus, currentPage, pageSize);
    }
  }, [id, userStatus, currentPage, pageSize, auth.user, fetchData]);
  
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUserStatus(e.target.value);
    setCurrentPage(1); // Reset to first page when changing filter
  };
  
  const handleRetry = () => {
    if (id) {
      fetchData(id, userStatus, currentPage, pageSize);
    }
  };
  
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };
  
  // Function to show prompt details modal with data loaded from direct API endpoint
  const handleViewPrompt = async (prompt: SystemMessageDto, event?: React.MouseEvent) => {
    // Prevent event propagation to avoid reload
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (!prompt.id || !auth.user) return;
    
    // Mostrar o prompt com dados básicos imediatamente para feedback visual
    setSelectedPrompt(prompt);
    
    // Depois atualizar com dados completos
    try {
      // Carregar os detalhes completos do prompt usando o endpoint direto
      console.log(`Fetching details for prompt ID: ${prompt.id}`);
      const promptDetails = await getPromptDirectById(auth.user, prompt.id);
      console.log("Prompt details fetched successfully:", promptDetails);
      
      // Atualizar os dados no modal
      setSelectedPrompt(promptDetails);
    } catch (err) {
      console.error('Error fetching prompt details:', err);
      // We're already showing basic data, so just report the error
      setError('Could not load all prompt details.');
    }
  };
  
  // Function to close the modal
  const handleCloseModal = () => {
    setSelectedPrompt(null);
  };
  
  // Handle delete button click
  const handleDeleteClick = (prompt: SystemMessageDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setPromptToDelete(prompt);
    setIsDeleteConfirmOpen(true);
  };
  
  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!promptToDelete || !promptToDelete.id || !id || !auth.user) {
      return;
    }
    
    setIsDeleting(true);
    
    try {
      await deleteApplicationPrompt(auth.user, id, promptToDelete.id);
      
      // Remove from the list
      setPrompts(prevPrompts => 
        prevPrompts.filter(p => p.id !== promptToDelete.id)
      );
      
      // Close confirm dialog
      setIsDeleteConfirmOpen(false);
      setPromptToDelete(null);
      
      // If we deleted all prompts on this page and it's not the first page,
      // go back one page
      if (prompts.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        // Otherwise just refresh the current page
        fetchData(id, userStatus, currentPage, pageSize);
      }
    } catch (err) {
      console.error('Error deleting prompt:', err);
      setError('Failed to delete the prompt. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Function to truncate long messages for display
  const truncateMessage = (message: string | undefined, maxLength: number = 150) => {
    if (!message) return 'N/A';
    
    return message.length > maxLength 
      ? `${message.substring(0, maxLength)}...` 
      : message;
  };
  
  // Function to navigate back to application details
  const handleBackToApplication = () => {
    navigate(`/applications/${id}`);
  };
  
  // Function used only for the delete confirmation dialog
  // The highlighting is now handled by the PromptDetailsView component
  
  return (
    <div>
      <PageHeader
        title={`System Messages - ${application?.name || 'Application'}`}
        breadcrumbs={[
          { label: "Applications", to: "/applications" },
          { label: application?.name || 'Application', to: `/applications/${id}` },
          { label: "System Messages" }
        ]}
        actions={
          <Button 
            variant="secondary"
            onClick={handleBackToApplication}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Application
          </Button>
        }
      />
      
      <div className="mb-6">
        <Button 
          variant="primary"
          onClick={() => navigate(`/applications/${id}/prompts/create`)}
        >
          <i className="fas fa-plus mr-2"></i>
          Create System Message
        </Button>
      </div>
      
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2"
            onClick={handleRetry}
          >
            Retry
          </Button>
        </div>
      )}
      
      <Card className="mb-6">
        <div className="p-6">
          <FormSelect
            label="Filter by User Status"
            id="userStatus"
            value={userStatus}
            onChange={handleFilterChange}
            options={[
              { value: "", label: "All Statuses" },
              ...availableUserStatuses.map(status => ({
                value: status,
                label: status === "NEW" ? "New Users" :
                       status === "ACTIVE" ? "Active Users" :
                       status === "INACTIVE" ? "Inactive Users" :
                       status === "BLOCKED" ? "Blocked Users" :
                       status === "ONBOARDING_IN_PROGRESS" ? "Onboarding" :
                       status
              }))
            ]}
            className="w-full md:w-64"
          />
        </div>
      </Card>
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : prompts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No system messages found for this application
              {userStatus && ` with status: ${userStatus}`}.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    User Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Updated At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {prompts.map((prompt) => (
                  <tr 
                    key={prompt.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => handleViewPrompt(prompt, e)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        prompt.user_status === 'NEW' ? 'bg-blue-100 text-blue-700' :
                        prompt.user_status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                        prompt.user_status === 'INACTIVE' ? 'bg-gray-100 text-gray-700' :
                        prompt.user_status === 'BLOCKED' ? 'bg-red-100 text-red-700' :
                        prompt.user_status === 'ONBOARDING_IN_PROGRESS' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-indigo-100 text-indigo-700' // default for 'ALL' or other statuses
                      }`}>
                        {prompt.user_status || 'ALL'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {truncateMessage(prompt.message)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prompt.created_at ? (
                        new Date(prompt.created_at).toLocaleDateString()
                      ) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prompt.updated_at ? (
                        new Date(prompt.updated_at).toLocaleDateString()
                      ) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="xs"
                          onClick={(e) => handleViewPrompt(prompt, e)}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/applications/${id}/prompts/edit/${prompt.id}`);
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="xs"
                          onClick={(e) => handleDeleteClick(prompt, e)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {prompts.length > 0 && totalPages > 1 && (
          <div className="py-3 px-6 border-t border-gray-200">
            <Pagination
              currentPage={currentPage}
              totalItems={prompts.length * totalPages}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
      
      {/* Modal para visualizar detalhes do prompt */}
      {selectedPrompt && (
        <PromptDetailsView
          prompt={selectedPrompt}
          isOpen={true}
          onClose={handleCloseModal}
        />
      )}
      
      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        title="Delete System Message"
        message={
          <>
            <p>Are you sure you want to delete this system message?</p>
            {promptToDelete?.user_status && (
              <p className="mt-2 font-medium">
                User Status: <span className="text-blue-600">{promptToDelete.user_status}</span>
              </p>
            )}
            <p className="mt-2 text-gray-600 text-sm border-l-4 border-gray-300 pl-3 italic">
              {truncateMessage(promptToDelete?.message, 100)}
            </p>
            <p className="mt-4 text-sm text-red-600">This action cannot be undone.</p>
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteConfirmOpen(false);
          setPromptToDelete(null);
        }}
      />
    </div>
  );
};

export default PromptsList;